import { useCallback, useState } from 'react';
import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables/TableWithStoredSettingsAndLayoutSettings';
import { PERMISSIONS_API } from '@Constants/backendConstants';
import { Permission } from '@Constants/Permission.g';
import { userColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useSession } from '@Contexts/session';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import type { ApiUser } from '@Model';
import { Button, iconSizeSm, type ToolbarOptions, useTableRefetch } from '@synqo/frontend-component-library';
import { IconUserPlus } from '@tabler/icons-react';
import { EnumFrontendLayoutSetting } from '@Model';
import { ImportUserModal } from './ImportUserModal';
import { useListApi } from '@Hooks/useListApi';
import { UsersRepository } from '@Repositories/Permissions';

export function FrameListUser() {
    const hasPermission = useHasPermission();
    const { partnerships } = useSession();
    const [isImportOpen, setIsImportOpen] = useState(false);
    const { refetchRef, refetchTable } = useTableRefetch();

    const module = useListApi(UsersRepository, PERMISSIONS_API);

    useRemoteCacheInvalidation('users', refetchTable);

    const canImport = hasPermission(Permission.PermissionsUsersCreate) && partnerships.length > 1;

    const toolbarElements = useCallback(
        (elements: ToolbarOptions[]) => [
            ...elements,
            canImport && (
                <Button key="importUser" appearance="outlined" variant="primary" onClick={() => setIsImportOpen(true)}>
                    <IconUserPlus size={iconSizeSm} />
                    &nbsp;Aus anderer Partnerschaft importieren
                </Button>
            ),
        ],
        [canImport],
    );

    return (
        <>
            <TableWithStoredSettingsAndLayoutSettings<ApiUser>
                layoutSettingsContext={EnumFrontendLayoutSetting.UsersTableLayout}
                name="UserList"
                columns={userColumns}
                loader={module.loadPage}
                refetchRef={refetchRef}
                canCreate={hasPermission(Permission.PermissionsUsersCreate)}
                useOnClickNavigation
                fullSize
                toolbarElements={toolbarElements}
            />
            <ImportUserModal isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} onImported={refetchTable} />
        </>
    );
}
